import { AdminMatches } from '@platform/client';
import { MatchGroupData } from '@platform/client/schemas';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useContext, useMemo } from 'react';
import { formatDateStr } from '../../../dashboard/my-projects/utils';
import { ProjectLink } from '../projects/ProjectsAdmin';
import { ProfessionalLink } from '../professionals/ProfessionalsAdmin';
import { NoRecordsFoundTabContent, ProfileTabContainer } from '../../components/ProfileComponents';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { MatchGroupLink } from '../matches/MatchesAdmin';
import { TeamLink } from '../teams/TeamsResultsTab';

const matchColumnHelper = createColumnHelper<MatchGroupData>();

export const MatchesTabContent = ({ organizationId }: { organizationId: number }) => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const {
    data: matchData,
    isLoading,
    isError,
    isSuccess,
  } = AdminMatches.useGetOrganizationAdminMatches(organizationId, {
    page: adminQueryOptionsV2.page,
    limit: adminQueryOptionsV2.limit,
    sort: adminQueryOptionsV2.sort,
  });
  const columns = useMemo(
    () =>
      [
        matchColumnHelper.accessor('id', {
          header: 'Id',
          cell: (info) => (
            <MatchGroupLink
              matchGroupId={info.getValue()}
              matchGroupName={info.getValue().toString()}
            />
          ),
          size: 5,
        }),
        matchColumnHelper.accessor('initiation_type', {
          id: 'initiation_types',
          header: 'Sent by',
          cell: (info) => info.getValue(),
          size: 10,
        }),
        matchColumnHelper.accessor('created_at', {
          header: 'Created',
          cell: (info) => formatDateStr(info.getValue()),
          size: 1,
        }),
        matchColumnHelper.accessor('project', {
          id: 'project_name',
          header: 'Project',
          cell: (project) => (
            <ProjectLink
              projectId={project.getValue()?.id}
              projectName={project.getValue()?.name}
            />
          ),
          size: 300,
        }),
        matchColumnHelper.accessor('representative', {
          id: 'representative_name',
          header: 'Representative',
          cell: (info) =>
            info.getValue()?.firstname + ' ' + info.getValue()?.lastname.charAt(0) + '.',
        }),
        matchColumnHelper.accessor('professionals', {
          header: 'Professional',
          cell: ({ row }) => {
            return row.original.team_id && row.original.cohort ?
                <TeamLink
                  teamId={row.original.team_id}
                  cohortId={row.original.cohort.id}
                  text={`Team #${row.original.team_id}`}
                />
              : <ProfessionalLink
                  userId={row.original.professionals[0].id}
                  name={
                    row.original.professionals[0].firstname +
                    ' ' +
                    row.original.professionals[0].lastname.charAt(0) +
                    '.'
                  }
                />;
          },
          enableSorting: false,
        }),
        matchColumnHelper.accessor('progress', {
          id: 'progresses',
          header: 'Progress',
          cell: (info) => info.getValue(),
          size: 100,
        }),
        matchColumnHelper.accessor('updated_at', {
          header: 'Updated',
          cell: (info) => formatDateStr(info.getValue()),
          size: 150,
        }),
      ] as ColumnDef<MatchGroupData>[],
    [],
  );

  return (
    <ProfileTabContainer>
      {isSuccess && matchData?.matches.length === 0 ?
        <NoRecordsFoundTabContent recordType='organization' />
      : <AdminTable
          isError={isError}
          isLoading={isLoading}
          isSuccess={isSuccess}
          columns={columns}
          data={matchData?.matches || []}
          isSimple
          filteredCount={matchData?.filtered_count ?? 0}
        />
      }
    </ProfileTabContainer>
  );
};
